import React, { useState, useEffect, useRef } from "react";
import { FaSearch, FaClock, FaTimes } from "react-icons/fa";
import styles from "../../styles/components/filters/SearchBar.module.css";
import { useCategories } from "../../contexts/CategoryContext";
import { useNavigate } from "react-router-dom";
import capitalizeFirst from "../../utils/capitalizeFirst";
import api from "../../utils/api/axiosInstance";
import getImageUrl from "../../utils/imageUtils";

const RECENT_SEARCHES_KEY = "simpary_recent_searches";
const MAX_RECENT_SEARCHES = 5;

const loadRecentSearches = () => {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const persistRecentSearch = (query, category, categoryId) => {
  if (!query.trim()) return loadRecentSearches();
  try {
    const current = loadRecentSearches();
    const deduped = current.filter(
      (s) => s.query.toLowerCase() !== query.trim().toLowerCase(),
    );
    const updated = [
      { query: query.trim(), category, categoryId, timestamp: Date.now() },
      ...deduped,
    ].slice(0, MAX_RECENT_SEARCHES);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
    return updated;
  } catch {
    return loadRecentSearches();
  }
};

const SearchBar = ({ compact = false, autoFocus = false }) => {
  const navigate = useNavigate();
  const { categories } = useCategories();
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [query, setQuery] = useState("");
  const [displayQuery, setDisplayQuery] = useState(""); // For autofill during keyboard navigation
  const [suggestions, setSuggestions] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState(() => loadRecentSearches());
  const [showRecentSearches, setShowRecentSearches] = useState(false);
  const [searching, setSearching] = useState(false);

  // Wrapper ref for button + dropdown, so outside click works correctly
  const dropdownWrapRef = useRef(null);
  const searchWrapperRef = useRef(null);
  const suggestionRefs = useRef([]);
  const isKeyboardNavigating = useRef(false);
  const originalQueryRef = useRef("");
  const inputRef = useRef(null);

  // Fetch suggestions when query or selected category changes (debounced on live input)
  useEffect(() => {
    if (isKeyboardNavigating.current) {
      return undefined;
    }

    const trimmedQuery = query.trim();
    if (trimmedQuery.length < 1) {
      setSuggestions([]);
      setShowSuggestions(false);
      if (query.length === 0) {
        setDisplayQuery("");
      }
      return undefined;
    }

    const currentQuery = query;

    setShowSuggestions(true);
    setShowRecentSearches(false);
    setSearching(true);

    let cancelled = false;
    const timer = setTimeout(async () => {
      if (isKeyboardNavigating.current) {
        return;
      }

      try {
        setSearching(true);
        const params = { query: trimmedQuery };

        if (selectedCategoryId) {
          params.categoryId = selectedCategoryId;
        } else if (selectedCategory && selectedCategory !== "All") {
          params.category = selectedCategory.trim();
        }

        const { data } = await api.get("/products/search", { params });

        if (!cancelled && !isKeyboardNavigating.current) {
          const results = Array.isArray(data) ? data : [];
          setSuggestions(results);
          setShowSuggestions(true);
          setHighlightedIndex(-1);
          originalQueryRef.current = currentQuery;
        }
      } catch (err) {
        console.error(
          "Error fetching search results:",
          err.response?.data || err.message,
        );
        if (!cancelled) {
          setSuggestions([]);
          setShowSuggestions(true);
        }
      } finally {
        if (!cancelled) {
          setSearching(false);
        }
      }
    }, 150);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, selectedCategory, selectedCategoryId]);

  useEffect(() => {
    if (!autoFocus) return undefined;
    const frame = requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [autoFocus]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownWrapRef.current &&
        !dropdownWrapRef.current.contains(e.target)
      ) {
        setIsDropdownOpen(false);
      }
      if (
        searchWrapperRef.current &&
        !searchWrapperRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
        setShowRecentSearches(false);
        setHighlightedIndex(-1);
        // Restore original query when clicking outside
        setDisplayQuery(query);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Navigate to product page when suggestion is clicked
  const handleSuggestionClick = (product) => {
    const searchTerm = (product.name || "").trim();
    const categoryName =
      (typeof product.category === "object" && product.category?.name) ||
      selectedCategory;
    const categoryId =
      (typeof product.category === "object" && product.category?._id) ||
      (typeof product.category === "string" ? product.category : null) ||
      selectedCategoryId;

    if (searchTerm) {
      const updated = persistRecentSearch(searchTerm, categoryName, categoryId);
      setRecentSearches(updated);
    }

    const navCategoryId = product.category?._id || product.category || "unknown";
    navigate(`/category/${navCategoryId}/product/${product._id}`);
    setQuery("");
    setDisplayQuery("");
    setSuggestions([]);
    setShowSuggestions(false);
    setShowRecentSearches(false);
    setHighlightedIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === "Enter") {
        handleSearch();
      } else if (e.key === "Escape") {
        setShowSuggestions(false);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        // Store original query when starting navigation
        if (highlightedIndex === -1) {
          originalQueryRef.current = query;
        }
        isKeyboardNavigating.current = true;
        setHighlightedIndex((prev) => {
          const nextIndex = prev < suggestions.length - 1 ? prev + 1 : prev;
          // Scroll into view if needed
          if (suggestionRefs.current[nextIndex]) {
            suggestionRefs.current[nextIndex].scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
          // Update display query (autofill) but don't trigger search
          if (nextIndex >= 0 && suggestions[nextIndex]?.name) {
            setDisplayQuery(suggestions[nextIndex].name);
          }
          return nextIndex;
        });
        // Reset flag after state update
        setTimeout(() => {
          isKeyboardNavigating.current = false;
        }, 0);
        break;

      case "ArrowUp":
        e.preventDefault();
        isKeyboardNavigating.current = true;
        setHighlightedIndex((prev) => {
          const nextIndex = prev > 0 ? prev - 1 : -1;
          // Scroll into view if needed
          if (nextIndex >= 0 && suggestionRefs.current[nextIndex]) {
            suggestionRefs.current[nextIndex].scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
          // Update display query (autofill) or restore original if at top
          if (nextIndex >= 0 && suggestions[nextIndex]?.name) {
            setDisplayQuery(suggestions[nextIndex].name);
          } else if (nextIndex === -1) {
            // Reset to original query when at top
            setDisplayQuery(originalQueryRef.current || query);
          }
          return nextIndex;
        });
        // Reset flag after state update
        setTimeout(() => {
          isKeyboardNavigating.current = false;
        }, 0);
        break;

      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          // Use the highlighted suggestion
          handleSuggestionClick(suggestions[highlightedIndex]);
        } else {
          // Use the current display query for search
          handleSearch();
        }
        break;

      case "Escape":
        e.preventDefault();
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        // Restore original query
        setDisplayQuery(originalQueryRef.current || query);
        break;

      default:
        // Reset highlighted index when typing
        if (e.key.length === 1) {
          setHighlightedIndex(-1);
        }
        break;
    }
  };

  const handleSearch = () => {
    const searchQuery = displayQuery || query;
    if (!searchQuery.trim()) return;

    const updated = persistRecentSearch(searchQuery, selectedCategory, selectedCategoryId);
    setRecentSearches(updated);

    const params = new URLSearchParams({ q: searchQuery });

    if (selectedCategoryId) {
      params.append("categoryId", selectedCategoryId);
    } else if (selectedCategory && selectedCategory !== "All") {
      params.append("category", selectedCategory);
    }

    navigate(`/search?${params.toString()}`);
    setSuggestions([]);
    setShowSuggestions(false);
    setShowRecentSearches(false);
    setHighlightedIndex(-1);
  };

  const handleRecentSearchClick = (recent) => {
    const params = new URLSearchParams({ q: recent.query });
    if (recent.categoryId) {
      params.append("categoryId", recent.categoryId);
    } else if (recent.category && recent.category !== "All") {
      params.append("category", recent.category);
    }
    navigate(`/search?${params.toString()}`);
    setShowRecentSearches(false);
    setQuery("");
    setDisplayQuery("");
  };

  const handleRemoveRecentSearch = (e, index) => {
    e.stopPropagation();
    const updated = recentSearches.filter((_, i) => i !== index);
    try {
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
    } catch {}
    setRecentSearches(updated);
  };

  const handleClearRecentSearches = (e) => {
    e.stopPropagation();
    try {
      localStorage.removeItem(RECENT_SEARCHES_KEY);
    } catch {}
    setRecentSearches([]);
    setShowRecentSearches(false);
  };

  return (
    <div ref={searchWrapperRef} className={styles.searchWrapper}>
      <div
        className={`${styles.searchbarWrapper} ${
          compact ? styles.searchbarWrapperCompact : ""
        }`}
        data-topbar-search={compact ? "true" : undefined}
        ref={dropdownWrapRef}
      >
        <button
          className={styles.dropdownButton}
          onClick={() => setIsDropdownOpen((v) => !v)}
        >
          {capitalizeFirst(selectedCategory)}
        </button>

        {isDropdownOpen && (
          <ul
            className={`${styles.dropdown} popupShadow`}
            onMouseLeave={() => setIsDropdownOpen(false)}
          >
            <li
              className={styles.dropdownList}
              onClick={() => {
                setSelectedCategory("All");
                setSelectedCategoryId(null);
                setIsDropdownOpen(false);
                setQuery("");
              }}
            >
              <span>All</span>
            </li>
            {categories.map((cat) => (
              <li
                key={cat._id}
                onClick={() => {
                  setSelectedCategory(cat.name);
                  setSelectedCategoryId(cat._id);
                  setIsDropdownOpen(false);
                  setQuery("");
                }}
                className={styles.dropdownList}
              >
                {capitalizeFirst(cat.name)}
              </li>
            ))}
          </ul>
        )}

        <input
          ref={inputRef}
          type="text"
          placeholder={`Search products${
            selectedCategory !== "All" ? ` in ${selectedCategory}` : " on Simpary"
          }...`}
          onChange={(e) => {
            const newValue = e.target.value;
            setQuery(newValue);
            setDisplayQuery(newValue);
            setHighlightedIndex(-1);
            if (newValue.trim().length >= 1) {
              setShowSuggestions(true);
              setShowRecentSearches(false);
            } else {
              setShowSuggestions(false);
              setShowRecentSearches(newValue.trim().length === 0);
            }
          }}
          onFocus={() => {
            if (query.trim().length >= 1) {
              setShowSuggestions(true);
              setShowRecentSearches(false);
            } else if (query.trim().length === 0) {
              setShowRecentSearches(true);
            }
          }}
          onKeyDown={handleKeyDown}
          className={styles.searchbarInput}
          value={displayQuery || query}
        />

        <button className={styles.searchIconArea} onClick={handleSearch}>
          <FaSearch />
        </button>
      </div>

      {showRecentSearches && recentSearches.length > 0 && (
        <div className={styles.recentSearchesWrapper}>
          <div className={styles.recentSearchesHeader}>
            <span className={styles.recentSearchesLabel}>
              <FaClock aria-hidden />
              Recent searches
            </span>
            <button
              className={styles.recentSearchesClear}
              onClick={handleClearRecentSearches}
              type="button"
            >
              Clear all
            </button>
          </div>
          <ul className={styles.recentSearchList}>
            {recentSearches.map((recent, index) => (
              <li
                key={recent.timestamp}
                className={styles.recentSearchItem}
                onClick={() => handleRecentSearchClick(recent)}
              >
                <FaClock className={styles.recentSearchIcon} aria-hidden />
                <div className={styles.recentSearchContent}>
                  <span className={styles.recentSearchTerm}>{recent.query}</span>
                  {recent.category && recent.category !== "All" && (
                    <span className={styles.recentSearchCategory}>
                      {recent.category}
                    </span>
                  )}
                </div>
                <button
                  className={styles.recentSearchRemove}
                  onClick={(e) => handleRemoveRecentSearch(e, index)}
                  type="button"
                  aria-label={`Remove "${recent.query}" from recent searches`}
                >
                  <FaTimes />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showSuggestions && query.trim().length > 0 && (
        <div className={styles.autocompleteWrapper}>
          <ul className={styles.autocompleteList}>
            {searching && suggestions.length === 0 && (
              <li className={styles.autocompleteItem}>Searching...</li>
            )}
            {!searching && suggestions.length === 0 && (
              <li className={styles.autocompleteEmpty}>
                <span className={styles.autocompleteEmptyTitle}>
                  No matching products
                </span>
                <span className={styles.autocompleteEmptyHint}>
                  Press Enter to search all products
                </span>
              </li>
            )}
            {suggestions.map((product, index) => (
              <li
                key={product._id}
                ref={(el) => (suggestionRefs.current[index] = el)}
                className={`${styles.autocompleteItem} ${
                  index === highlightedIndex
                    ? styles.autocompleteItemHighlighted
                    : ""
                }`}
                onClick={() => handleSuggestionClick(product)}
                onMouseEnter={() => {
                  setHighlightedIndex(index);
                  // Store original query if starting hover navigation
                  if (highlightedIndex === -1) {
                    originalQueryRef.current = query;
                  }
                  // Autofill on hover too
                  if (product?.name) {
                    setDisplayQuery(product.name);
                  }
                }}
              >
                {product.images?.[0] ? (
                  <img
                    src={getImageUrl(product.images[0])}
                    alt={product.name || "Product"}
                    className={styles.suggestionImage}
                  />
                ) : (
                  <div className={styles.suggestionImagePlaceholder} aria-hidden />
                )}
                <div className={styles.suggestionContent}>
                  <span className={styles.suggestionName}>
                    {product.name || "Unnamed Product"}
                  </span>
                  {product.brand && (
                    <span className={styles.suggestionBrand}>
                      {typeof product.brand === "object"
                        ? product.brand.name
                        : product.brand}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
